import { CharacterTest } from './types'
import { keepOnly } from './stringUtils'

/**
 * Transposes the cursor position from the inputted text to the formatted text.
 *
 * @returns the new cursor position.
 *
 * Given
 *   inputText = "$1234.00 USD"
 *   inputCursorPosition = 3
 *   FormattedText = $1,234.00 USD
 *   charIsRelevant = digits only
 *   anchor = The decimal point
 *
 * The function will return a new cursor position of 4
 *
 * We can visualize the input text and cursor position as follows:
 * $12|34.00 USD
 *
 * The formatted text with resulting cursor position would look like this:
 * $1,2|34.00 USD
 *
 */
export const transposeCursorPosition = (
  action: { inputText: string; inputCursorPosition: number },
  formattedText: string,
  charIsRelevant: CharacterTest,
  getAnchor: (text: string) => number
): number => {
  const relativePosition = getRelativePosition(
    action.inputText,
    charIsRelevant,
    getAnchor(action.inputText),
    action.inputCursorPosition
  )
  const absolutePosition = getAbsolutePosition(
    formattedText,
    charIsRelevant,
    getAnchor(formattedText),
    relativePosition
  )
  return absolutePosition
}

/*

    Anchor refers to a character in a string, while position is a place between characters.
    Both are described with a number.

    An anchor of 0 refers to the first character.
    [A]BC

    TODO: Use -1 to anchor to the beginning of a string
    []ABC

    To anchor to the end of a string, provide a number that is equal to or greater than the string's length.
    Infinity will anchor to the end of any string.
    ABC[]

    Relative position measures the distance from the anchor to a target position.
    A relative position of 1 is just after the anchor.
    A[B]|C

    A relative position of 0 is the position just before the anchor.
    A|[B]C

    Absolute position is measured from the start of the string.

    Characters can be skipped over when counting a distance.
    Let's say we only want to count letters:

    A[B]C,,,DE|F == 4                    The relative position is 4
    A[B]C,,,DEF    +4 == A[B]C,,,DE|F    The absolute position is 8

*/

/**
 * @param str - The subject of the function.
 * @param charIsCounted - Tests a character to determine if it should be included in the returned count.
 * @param anchor - The beginning (-1) or end (Infinity) of a string, or a character index.
 * @param targetPosition - A location between characters, starting with 0 for the beginning of the string.
 *
 * @returns The distance between the anchor and target position.
 */
export const getRelativePosition = (
  str: string,
  charIsCounted: CharacterTest,
  anchor: number,
  inputCursorPosition: number
): number => {
  const clampedAnchor = clamp(anchor, [0, str.length])
  const clampedTarget = clamp(inputCursorPosition, [0, str.length])

  if (clampedAnchor === clampedTarget) {
    return 0
  }

  const [first, last] = [clampedAnchor, clampedTarget].sort((a, b) => a - b)

  /**
   * xxA123|4
   * forward: A123
   *
   * xx|123A4
   * backward: 123
   */
  const slice = str.slice(first, last)

  const returnVal =
    clampedAnchor < clampedTarget
      ? keepOnly(
          slice,
          // When counting forwards the anchor is included because slice is.
          (...args) => charIsCounted(...args) || args[1] === 0
        ).length
      : keepOnly(slice, charIsCounted).length * -1 || 0
  // After counting backwards invert the result. Don't invert 0 to -0.

  return returnVal
}

/**
 * @param str - The subject of the function
 * @param charIsCounted - Tests a character to determine if it is included in the relative position.
 * @param anchor - The beginning (-1) or end (Infinity) of a string, or a character index.
 * @param relativePosition - The distance between the anchor and target position.
 *
 * @returns The resulting position after moving a relative distance from the anchor.
 */
export const getAbsolutePosition = (
  str: string,
  charIsCounted: CharacterTest,
  anchor: number,
  relativePosition: number
): number => {
  const clampedAnchor = clamp(anchor, [0, str.length])

  if (relativePosition === 0) {
    return clampedAnchor
  }
  if (relativePosition === 1) {
    return clampedAnchor + 1
  }

  let distanceRemaining = Math.abs(relativePosition)
  let lastCountedIndex: number | null = null

  if (relativePosition < 0) {
    // step backwards
    for (let i = clampedAnchor - 1; i >= 0; i--) {
      if (charIsCounted(str[i], i, str)) {
        distanceRemaining--
        lastCountedIndex = i
        if (distanceRemaining === 0) {
          return i
        }
      }
    }
    return lastCountedIndex || 0
  }

  // step forwards
  for (let i = clampedAnchor + 1; i <= str.length; i++) {
    if (charIsCounted(str[i], i, str)) {
      distanceRemaining--
      lastCountedIndex = i
      if (distanceRemaining === 0) {
        return i
      }
    }
  }

  // lastCountedIndex + 1 places the cursor after the last user
  // entered/controlled value if the last 'charIsCounted' is index=4
  // to place the cursor after we +1
  //
  // e.g. 0123[4]----  will place cursor 01234|----
  return lastCountedIndex ? lastCountedIndex + 1 : str.length
}

/**
 * Limit a number to a given range.
 */
export const clamp = (
  n: number,
  boundaries: [LowerBoundary, UpperBoundary]
) => {
  const [lower, upper] = boundaries
  if (n < lower) {
    return lower
  }
  if (n > upper) {
    return upper
  }
  return n
}

type LowerBoundary = number
type UpperBoundary = number
